/**
 * Commission Calculator Page
 * Interactive payout calculator with cascading splits.
 * Matches the Commission Calculator.html mockup.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Calculator, DollarSign, TrendingUp, Users, ArrowRight,
  Download, RotateCcw, ChevronRight
} from 'lucide-react';

const QUICK_SCENARIOS = [
  { label: 'Small Merchant', gross: 500, cost: 100, splits: [90, 70, 80, 50] },
  { label: 'Mid-Size', gross: 2000, cost: 400, splits: [90, 70, 80, 50] },
  { label: 'High Volume', gross: 10000, cost: 2000, splits: [90, 70, 80, 50] },
  { label: 'Equal Split', gross: 1000, cost: 200, splits: [50, 50, 50, 50] },
];

const ROLE_LABELS = ['ISO', 'Agent', 'Subagent'];

export default function CommissionCalculator() {
  const { user } = useAuthStore();
  const [gross, setGross] = useState(1000);
  const [costs, setCosts] = useState(200);
  const [chain, setChain] = useState([
    { label: 'ISO', split_pct: 90 },
    { label: 'Agent', split_pct: 70 },
    { label: 'Subagent', split_pct: 80 },
  ]);
  const [result, setResult] = useState(null);
  const [userChain, setUserChain] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load user's actual split chain
  useEffect(() => {
    (async () => {
      try {
        const data = await api.request('/residuals/calculate/chain');
        if (data.chain && data.chain.length > 0) {
          setUserChain(data.chain);
          setChain(data.chain.map(c => ({
            label: c.label || c.role,
            split_pct: c.split_pct,
          })));
        }
      } catch (err) {
        // Use defaults
      }
    })();
  }, []);

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/residuals/calculate', {
        method: 'POST',
        body: JSON.stringify({
          gross_profit: gross,
          processing_costs: costs,
          split_chain: chain.filter(c => c.split_pct > 0),
        }),
      });
      setResult(data);
    } catch (err) {
      console.error('Calculation error:', err);
    }
    setLoading(false);
  }, [gross, costs, chain]);

  useEffect(() => { calculate(); }, [calculate]);

  const updateChainLevel = (idx, pct) => {
    setChain(prev => prev.map((c, i) => i === idx ? { ...c, split_pct: pct } : c));
  };

  const addLevel = () => {
    const nextLabel = ROLE_LABELS[chain.length] || `Level ${chain.length + 1}`;
    setChain(prev => [...prev, { label: nextLabel, split_pct: 50 }]);
  };

  const removeLevel = (idx) => {
    if (chain.length <= 1) return;
    setChain(prev => prev.filter((_, i) => i !== idx));
  };

  const applyScenario = (scenario) => {
    setGross(scenario.gross);
    setCosts(scenario.cost);
    setChain(scenario.splits.map((pct, i) => ({
      label: ROLE_LABELS[i] || `Level ${i + 1}`,
      split_pct: pct,
    })));
  };

  const resetCalc = () => {
    setGross(1000);
    setCosts(200);
    setChain([
      { label: 'ISO', split_pct: 90 },
      { label: 'Agent', split_pct: 70 },
      { label: 'Subagent', split_pct: 80 },
    ]);
  };

  const netProfit = gross - costs;

  return (
    <div className="space-y-6" data-testid="commission-calculator">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div>
            <h1 className="text-base sm:text-xl font-bold" data-testid="calc-page-title">Cascading Payout Calculator</h1>
            <p className="text-blue-100 text-xs sm:text-sm">Model your revenue splits across the entire hierarchy</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Panel (2/3) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Input Parameters */}
          <div className="bg-card border rounded-xl p-5 space-y-4">
            <h2 className="font-semibold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              Input Parameters
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Gross Profit</label>
                <p className="text-xs text-muted-foreground mb-1">Total profit from processors</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    value={gross}
                    onChange={e => setGross(parseFloat(e.target.value) || 0)}
                    className="pl-7"
                    step="0.01"
                    data-testid="gross-profit-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Processing Costs</label>
                <p className="text-xs text-muted-foreground mb-1">Total processing expenses</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    value={costs}
                    onChange={e => setCosts(parseFloat(e.target.value) || 0)}
                    className="pl-7"
                    step="0.01"
                    data-testid="processing-costs-input"
                  />
                </div>
              </div>
            </div>

            {/* Net Profit Display */}
            <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Net Profit</span>
              <span className="text-lg font-bold" data-testid="net-profit-display">
                ${netProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* Split Chain Builder */}
          <div className="bg-card border rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Split Chain (Cascading)
              </h2>
              <Button variant="outline" size="sm" onClick={addLevel} data-testid="add-level-btn">
                + Add Level
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Each level receives a % of the level above. For example: ISO gets 90% of total, Agent gets 70% of ISO's share.
            </p>

            <div className="space-y-3">
              {chain.map((level, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg" data-testid={`chain-level-${idx}`}>
                  {idx > 0 && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <Input
                      value={level.label}
                      onChange={e => {
                        const newChain = [...chain];
                        newChain[idx].label = e.target.value;
                        setChain(newChain);
                      }}
                      className="h-8 text-sm font-medium"
                      data-testid={`chain-label-${idx}`}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={level.split_pct}
                      onChange={e => updateChainLevel(idx, parseFloat(e.target.value) || 0)}
                      className="w-20 h-8 text-sm text-center"
                      data-testid={`chain-pct-${idx}`}
                    />
                    <span className="text-sm font-medium text-muted-foreground">%</span>
                  </div>
                  <span className="text-xs text-muted-foreground min-w-[80px] text-right">
                    {idx === 0 ? 'of net profit' : `of ${chain[idx-1].label}'s`}
                  </span>
                  {chain.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-600 h-8 w-8 p-0"
                      onClick={() => removeLevel(idx)}
                    >
                      &times;
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Quick Scenarios */}
          <div className="bg-card border rounded-xl p-5 space-y-3">
            <h2 className="font-semibold text-sm">Quick Scenarios</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {QUICK_SCENARIOS.map((s) => (
                <button
                  key={s.label}
                  onClick={() => applyScenario(s)}
                  className="p-3 rounded-lg border text-left hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid={`scenario-${s.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <p className="text-xs font-semibold">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    ${s.gross.toLocaleString()} gross, ${s.cost} costs
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Panel (1/3) */}
        <div className="space-y-5">
          <div className="bg-card border rounded-xl p-5 space-y-4 sticky top-20">
            <h2 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Results
            </h2>

            {result && (
              <div className="space-y-3">
                {/* Net Profit */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Profit</p>
                  <p className="text-xl font-bold" data-testid="result-net-profit">${result.net_profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>

                {/* Waterfall Breakdown */}
                <div className="space-y-2">
                  {/* Super Admin keeps */}
                  {result.breakdown.length > 0 && (
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-purple-600">Super Admin Keeps</p>
                          <p className="text-sm text-muted-foreground">{100 - result.breakdown[0].split_pct}% of net</p>
                        </div>
                        <p className="text-lg font-bold text-purple-700" data-testid="result-sa-keeps">
                          ${result.breakdown[0].keeps_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  )}

                  {result.breakdown.map((level, idx) => (
                    <div key={idx} className={`rounded-lg p-3 ${
                      idx === 0 ? 'bg-blue-50' : idx === 1 ? 'bg-green-50' : 'bg-orange-50'
                    }`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className={`text-[10px] uppercase tracking-wider ${
                            idx === 0 ? 'text-blue-600' : idx === 1 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {level.label} receives
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {level.split_pct}% {idx === 0 ? 'of net' : `of ${result.breakdown[idx-1].label}`} = {level.effective_rate}% effective
                          </p>
                        </div>
                        <p className={`text-lg font-bold ${
                          idx === 0 ? 'text-blue-700' : idx === 1 ? 'text-green-700' : 'text-orange-700'
                        }`} data-testid={`result-level-${idx}`}>
                          ${level.share_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      {/* What this level keeps vs passes down */}
                      {idx < result.breakdown.length - 1 && (
                        <div className="mt-2 pt-2 border-t border-black/5 flex justify-between text-xs text-muted-foreground">
                          <span>Keeps: ${level.keeps_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          <span>Passes: ${level.share_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Effective Rates */}
                <div className="bg-card border rounded-lg p-3 space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Effective Rates</h3>
                  {result.breakdown.map((level, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{level.label}</span>
                      <span className="font-semibold">{level.effective_rate}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={resetCalc} data-testid="reset-calc-btn">
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reset
              </Button>
              <Button variant="outline" size="sm" className="flex-1" data-testid="export-calc-btn">
                <Download className="h-3.5 w-3.5 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
